/**
 * 2D-Rahmenvorschau für die Editor-Kacheln (ohne three.js): zeichnet Schattenfuge, Leiste,
 * Passepartout bzw. Posterleisten maßstäblich um das Druckformat. Geometrie wie build.ts:
 * - Schattenfuge: Leiste beginnt `gapMm` außerhalb des Bildes
 * - klassisch:    Passepartout (lang/kurz) um das Bild, Leiste überdeckt den Inhalt um `rebateMm`
 * - Posterleiste: Leisten oben/unten, überdecken das Poster um `rebateMm`
 */
import type { LibraryMaterial, Preview3dLibrary, ResolvedPreview3d } from './types.ts'

export type Frame2d = {
  type: 'shadow-gap' | 'classic' | 'poster-rail'
  shape: string
  /** Druckformat (Bildfläche) in mm, wie der Ausschnitt ausgerichtet */
  printW: number
  printH: number
  faceMm: number
  gapMm: number
  rebateMm: number
  /** Passepartout links/rechts bzw. oben/unten */
  matX: number
  matY: number
  bar: MaterialLook
  mat: MaterialLook
  glass: boolean
}

type MaterialLook = { color: string; textureUrl: string | null; tileMm: number; procedural: string; metal: boolean }

const look = (lib: Preview3dLibrary, slug: string, fallback: string): MaterialLook => {
  const m: LibraryMaterial | undefined = lib.materials.find((x) => x.slug === slug)
  return {
    color: m?.color || fallback,
    textureUrl: m?.colorMapUrl || null,
    tileMm: Math.max(5, Number(m?.tileCm || 20) * 10),
    procedural: m?.procedural || 'none',
    metal: Number(m?.metalness || 0) > 0.5,
  }
}

/** Aufgelöster 3D-Zustand + Druckmaß → 2D-Rahmen (null = kein Rahmen) */
export function frame2dFromState(state: ResolvedPreview3d | null, library: Preview3dLibrary, printMm: { w: number; h: number } | null): Frame2d | null {
  const f = state?.frame
  if (!f || !printMm || !(printMm.w > 0) || !(printMm.h > 0)) return null
  const classic = f.type === 'classic'
  const matLong = classic ? Math.max(0, Number(f.matLongMm || 0)) : 0
  const matShort = classic ? Math.max(0, Number(f.matShortMm || 0)) : 0
  const landscape = printMm.w >= printMm.h
  return {
    type: f.type,
    shape: f.shape || 'flat',
    printW: printMm.w,
    printH: printMm.h,
    faceMm: Math.max(1, Number(f.faceWidthMm || 0)),
    gapMm: f.type === 'shadow-gap' ? Math.max(0, Number(f.gapMm || 0)) : 0,
    rebateMm: f.type === 'shadow-gap' ? 0 : Math.max(0, Number(f.rebateMm || 0)),
    matX: landscape ? matLong : matShort,
    matY: landscape ? matShort : matLong,
    bar: look(library, f.material, '#8a6f52'),
    mat: look(library, f.matMaterial || 'passepartout-weiss', '#f7f5ef'),
    glass: classic && !!f.glassMaterial,
  }
}

/** Wie weit der Rahmen über das Druckformat hinausragt (mm, je Seite: x = links/rechts, y = oben/unten) */
export function frame2dOverhangMm(f: Frame2d): { x: number; y: number } {
  if (f.type === 'poster-rail') return { x: 0, y: Math.max(0, f.faceMm - f.rebateMm) }
  const ring = f.type === 'shadow-gap' ? f.gapMm + f.faceMm : f.faceMm - f.rebateMm
  return { x: f.matX + ring, y: f.matY + ring }
}

/** Anteil (0–0.4) der Kachel, der für den Rahmen freigehalten wird – bezogen auf die längere Druckseite */
export function frame2dPadFraction(f: Frame2d | null): number {
  if (!f) return 0
  const o = frame2dOverhangMm(f)
  const long = Math.max(f.printW, f.printH)
  // etwas Luft (12 %), damit die Leiste nicht am Kachelrand anstößt
  return Math.min(0.4, (1.12 * Math.max(o.x, o.y)) / Math.max(1, long + 2 * Math.max(o.x, o.y)) + 0.01)
}

// ---------- Texturen (einmal laden, danach neu zeichnen) ----------
const textures = new Map<string, HTMLImageElement | 'error' | Set<() => void>>()
function texture(url: string, onReady: () => void): HTMLImageElement | null {
  const hit = textures.get(url)
  if (hit instanceof HTMLImageElement) return hit
  if (hit === 'error') return null
  if (hit) {
    // lädt noch: alle Zeichenflächen, die darauf warten, neu zeichnen lassen
    hit.add(onReady)
    return null
  }
  const waiting = new Set<() => void>([onReady])
  textures.set(url, waiting)
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.onload = () => {
    textures.set(url, img)
    waiting.forEach((cb) => cb())
  }
  img.onerror = () => textures.set(url, 'error')
  img.src = url
  return null
}

function fillFor(ctx: CanvasRenderingContext2D, m: MaterialLook, pxPerMm: number, vertical: boolean, onReady: () => void): string | CanvasPattern {
  if (!m.textureUrl) return m.color
  const img = texture(m.textureUrl, onReady)
  if (!img) return m.color
  const pat = ctx.createPattern(img, 'repeat')
  if (!pat) return m.color
  const s = (m.tileMm * pxPerMm) / Math.max(1, img.naturalWidth)
  const mtx = new DOMMatrix()
  if (vertical) mtx.rotateSelf(90)
  mtx.scaleSelf(s, s)
  pat.setTransform(mtx)
  return pat
}

type Rect = { x: number; y: number; w: number; h: number }

/** Ring zwischen zwei Rechtecken mit Gehrung, je Seite eigener Füllstil */
function mitredRing(ctx: CanvasRenderingContext2D, outer: Rect, inner: Rect, side: (s: 'top' | 'bottom' | 'left' | 'right') => string | CanvasPattern | CanvasGradient) {
  const o = { l: outer.x, t: outer.y, r: outer.x + outer.w, b: outer.y + outer.h }
  const i = { l: inner.x, t: inner.y, r: inner.x + inner.w, b: inner.y + inner.h }
  const quads: Array<['top' | 'bottom' | 'left' | 'right', number[]]> = [
    ['top', [o.l, o.t, o.r, o.t, i.r, i.t, i.l, i.t]],
    ['bottom', [o.l, o.b, o.r, o.b, i.r, i.b, i.l, i.b]],
    ['left', [o.l, o.t, i.l, i.t, i.l, i.b, o.l, o.b]],
    ['right', [o.r, o.t, i.r, i.t, i.r, i.b, o.r, o.b]],
  ]
  for (const [name, p] of quads) {
    ctx.beginPath()
    ctx.moveTo(p[0], p[1]); ctx.lineTo(p[2], p[3]); ctx.lineTo(p[4], p[5]); ctx.lineTo(p[6], p[7])
    ctx.closePath()
    ctx.fillStyle = side(name)
    ctx.fill()
  }
}

const grow = (r: Rect, dx: number, dy: number): Rect => ({ x: r.x - dx, y: r.y - dy, w: r.w + 2 * dx, h: r.h + 2 * dy })

/**
 * Rahmen um `print` (Pixel-Rechteck des Druckformats) zeichnen. `onTexture` wird aufgerufen,
 * sobald eine Textur nachgeladen ist (dann neu zeichnen).
 */
export function drawFrame2d(ctx: CanvasRenderingContext2D, print: Rect, f: Frame2d, onTexture: () => void = () => {}) {
  if (!(print.w > 4) || !(print.h > 4)) return
  const k = print.w / f.printW // px je mm
  ctx.save()

  if (f.type === 'poster-rail') {
    const face = f.faceMm * k
    const r = f.rebateMm * k
    const bar = fillFor(ctx, f.bar, k, false, onTexture)
    for (const top of [true, false]) {
      const y = top ? print.y - face + r : print.y + print.h - r
      ctx.fillStyle = bar
      ctx.fillRect(print.x - 1, y, print.w + 2, face)
      shadeBar(ctx, { x: print.x - 1, y, w: print.w + 2, h: face }, 'h', f.shape)
    }
    ctx.restore()
    return
  }

  const content = grow(print, f.matX * k, f.matY * k)
  // Passepartout (unter der Leiste, Fensterkante mit feiner Schattenlinie)
  if (f.matX > 0 || f.matY > 0) {
    const under = grow(content, f.rebateMm * k, f.rebateMm * k)
    ctx.fillStyle = fillFor(ctx, f.mat, k, false, onTexture)
    ctx.beginPath()
    ctx.rect(under.x, under.y, under.w, under.h)
    ctx.rect(print.x, print.y, print.w, print.h)
    ctx.fill('evenodd')
    ctx.strokeStyle = 'rgba(0,0,0,0.28)'
    ctx.lineWidth = Math.max(0.6, 0.8 * k)
    ctx.strokeRect(print.x, print.y, print.w, print.h)
  }

  let barInner: Rect
  if (f.type === 'shadow-gap') {
    const gapOuter = grow(content, f.gapMm * k, f.gapMm * k)
    // Fuge: dunkel, zur Leiste hin am dunkelsten
    mitredRing(ctx, gapOuter, content, (s) => {
      const g = s === 'top' ? ctx.createLinearGradient(0, gapOuter.y, 0, content.y)
        : s === 'bottom' ? ctx.createLinearGradient(0, gapOuter.y + gapOuter.h, 0, content.y + content.h)
          : s === 'left' ? ctx.createLinearGradient(gapOuter.x, 0, content.x, 0)
            : ctx.createLinearGradient(gapOuter.x + gapOuter.w, 0, content.x + content.w, 0)
      g.addColorStop(0, '#0b0b0c')
      g.addColorStop(1, '#3a3a3d')
      return g
    })
    barInner = gapOuter
  } else {
    barInner = grow(content, -f.rebateMm * k, -f.rebateMm * k)
  }
  const barOuter = grow(barInner, f.faceMm * k, f.faceMm * k)
  const hFill = fillFor(ctx, f.bar, k, false, onTexture)
  const vFill = fillFor(ctx, f.bar, k, true, onTexture)
  mitredRing(ctx, barOuter, barInner, (s) => (s === 'top' || s === 'bottom' ? hFill : vFill))
  // Licht von oben links: obere/linke Leiste heller, untere/rechte dunkler
  mitredRing(ctx, barOuter, barInner, (s) => (s === 'top' ? 'rgba(255,255,255,0.10)' : s === 'left' ? 'rgba(255,255,255,0.05)' : s === 'right' ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.14)'))
  profileShading(ctx, barOuter, barInner, f.shape, f.faceMm * k)
  // Gehrungsfugen + Kanten
  ctx.strokeStyle = 'rgba(0,0,0,0.25)'
  ctx.lineWidth = Math.max(0.5, 0.25 * k)
  ctx.beginPath()
  ctx.moveTo(barOuter.x, barOuter.y); ctx.lineTo(barInner.x, barInner.y)
  ctx.moveTo(barOuter.x + barOuter.w, barOuter.y); ctx.lineTo(barInner.x + barInner.w, barInner.y)
  ctx.moveTo(barOuter.x, barOuter.y + barOuter.h); ctx.lineTo(barInner.x, barInner.y + barInner.h)
  ctx.moveTo(barOuter.x + barOuter.w, barOuter.y + barOuter.h); ctx.lineTo(barInner.x + barInner.w, barInner.y + barInner.h)
  ctx.stroke()
  ctx.strokeStyle = 'rgba(0,0,0,0.35)'
  ctx.lineWidth = Math.max(0.6, 0.3 * k)
  ctx.strokeRect(barOuter.x, barOuter.y, barOuter.w, barOuter.h)
  // Schlagschatten der Leiste auf Bild/Passepartout
  if (f.type !== 'shadow-gap') {
    const sh = Math.max(1, Math.min(4 * k, 6))
    ctx.fillStyle = 'rgba(0,0,0,0.18)'
    ctx.fillRect(barInner.x, barInner.y, barInner.w, sh)
    ctx.fillRect(barInner.x, barInner.y + sh, sh, barInner.h - sh)
  }
  if (f.glass) {
    const g = ctx.createLinearGradient(barInner.x, barInner.y, barInner.x + barInner.w, barInner.y + barInner.h)
    g.addColorStop(0.28, 'rgba(255,255,255,0)')
    g.addColorStop(0.4, 'rgba(255,255,255,0.16)')
    g.addColorStop(0.5, 'rgba(255,255,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(barInner.x, barInner.y, barInner.w, barInner.h)
  }
  ctx.restore()
}

/** Profilform andeuten: abgerundet = Verlauf quer zur Leiste, gestuft = Kante, abgeschrägt = helle Innenfase */
function profileShading(ctx: CanvasRenderingContext2D, outer: Rect, inner: Rect, shape: string, facePx: number) {
  if (shape === 'stepped' || shape === 'bevel') {
    const mid = grow(inner, facePx * 0.45, facePx * 0.45)
    if (shape === 'stepped') {
      ctx.strokeStyle = 'rgba(0,0,0,0.3)'
      ctx.lineWidth = Math.max(0.5, facePx * 0.04)
      ctx.strokeRect(mid.x, mid.y, mid.w, mid.h)
    } else {
      mitredRing(ctx, mid, inner, (s) => (s === 'top' || s === 'left' ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.12)'))
    }
    return
  }
  if (shape !== 'rounded') return
  mitredRing(ctx, outer, inner, (s) => {
    const g = s === 'top' ? ctx.createLinearGradient(0, outer.y, 0, inner.y)
      : s === 'bottom' ? ctx.createLinearGradient(0, outer.y + outer.h, 0, inner.y + inner.h)
        : s === 'left' ? ctx.createLinearGradient(outer.x, 0, inner.x, 0)
          : ctx.createLinearGradient(outer.x + outer.w, 0, inner.x + inner.w, 0)
    g.addColorStop(0, 'rgba(0,0,0,0.18)')
    g.addColorStop(0.45, 'rgba(255,255,255,0.14)')
    g.addColorStop(1, 'rgba(0,0,0,0.2)')
    return g
  })
}

function shadeBar(ctx: CanvasRenderingContext2D, r: Rect, dir: 'h', shape: string) {
  const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h)
  g.addColorStop(0, 'rgba(255,255,255,0.14)')
  g.addColorStop(shape === 'rounded' ? 0.5 : 0.85, 'rgba(255,255,255,0)')
  g.addColorStop(1, 'rgba(0,0,0,0.22)')
  ctx.fillStyle = g
  ctx.fillRect(r.x, r.y, r.w, r.h)
  ctx.strokeStyle = 'rgba(0,0,0,0.3)'
  ctx.lineWidth = 0.75
  ctx.strokeRect(r.x, r.y, r.w, r.h)
  void dir
}
